import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PusherService } from '@rubii/frontend-common';
import { AiAgent, AiThread, AiTool } from '@rubii/entity/src/mdb-entities/ai';
import { ClientAgentRunner, ClientToolRunner } from '@rubii/sdk-web-openai/src/classes';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LlmVendor } from '@rubii/sdk-web-openai/src/types/vendor-llm.type';
import {ConfirmComponent} from '@rubii/frontend-common';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmData } from '@rubii/frontend-common/lib/components/confirm/confirm.component';
import { MarkdownModalComponent, MarkdownModalData } from './markdown-modal.component';
import { ClientService } from '../../../../services/client.service';
import { MarkdownService } from '../../../../services/markdown.service';
import { Location } from '@angular/common';
import { UtilService } from '../../../../services/util.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-ai-agent',
  templateUrl: './ai-agent.component.html',
  styleUrls: ['./ai-agent.component.scss']
})
export class AiAgentComponent implements OnInit, OnDestroy {
  vendors: LlmVendor[] = [];
  models: Record<LlmVendor, string[]> = {} as Record<LlmVendor, string[]>;

  agents: AiAgent[] = [];
  filteredAgents: AiAgent[] = [];
  selectedAgent: AiAgent | null = null;

  tools: AiTool[] = [];

  loading = false;
  agentForm: FormGroup;
  searchQuery: string = '';
  isCreatingNew = false;

  agentRunner: ClientAgentRunner;
  toolRunner: ClientToolRunner;

  subscriptions: Subscription[] = [];

  constructor(
    private client: ClientService,
    private cdRef: ChangeDetectorRef,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private markdownService: MarkdownService,
    private location: Location,
    private utilService: UtilService
  ) {
    this.initForm();
  }

  private initForm() {
    this.agentForm = this.fb.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      llmVendor: ['', Validators.required],
      llmModel: ['', Validators.required],
      systemPrompt: ['', Validators.required],
      topP: [null],
      temperature: [0.7],
      maxPromptTokens: [null],
      maxCompletionTokens: [null],
      vectorStoreId: [null],
      vendorAssistantIsShared: [false],
      vendorAssistantId: [null],
      vectorStoreIsShared: [false],
      promptCache: [false],
      asTool: [false],
      toolChoice: ['auto'],
      reasoningEffort: [null],
      tools: [[]],
      contextCompact: ['none'],
      manualUpdate: [false],
      templateKeys: ['']
    });
  }

  async ngOnInit() {
    await this.getAgentList();
    await this.getVendors();
    await this.getToolList();

    // Subscribe to vendor changes to load models
    this.agentForm.get('llmVendor').valueChanges.subscribe(async (vendor: LlmVendor) => {
      if (vendor) {
        const models = await this.getModels(vendor);
        this.agentForm.patchValue({ llmModel: '' }); // Reset model when vendor changes
        this.cdRef.detectChanges();
      }
    })

    // router using query params agent_id
    this.subscriptions.push(this.route.queryParams.subscribe(async (params) => {
      if (+params.agent_id !== this.selectedAgent?._id) {
        await this.selectAgent(this.agents.find(agent => agent._id == params.agent_id));
      }

      if (params['sub-tab'] === 'ai-agents' && this.agents.length > 0 && !this.selectedAgent) {
        // Auto-select first agent when visiting AI Agents tab without agent_id
        await this.selectAgent(this.agents[0]);
      }
    }));
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  async getVendors() {
    const vendors = await this.client.llm.listVendors();
    this.vendors = vendors;
  }

  async getModels(vendor: LlmVendor) {
    if (this.models[vendor]) {  
      return this.models[vendor];
    } else {
      const models = await this.client.llm.listModels({ vendor });
      this.models[vendor] = models;
      return models;
    }
  }

  async getAgentList() {
    try {
      this.loading = true;
      const tempRunner = await this.client.ai.createAgentRunner('');
      this.agents = await tempRunner.listAvailableAgents();
      this.agents.sort((a, b) => new Date(b.updatedate).getTime() - new Date(a.updatedate).getTime());
      this.filterAgents();
      this.cdRef.detectChanges();
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      this.loading = false;
    }
  }

  async getToolList() {
    this.toolRunner = await this.client.ai.createToolRunner('');
    this.tools = await this.toolRunner.listAvailableTools();
  }

  filterAgents() {
    if (!this.searchQuery) {
      this.filteredAgents = [...this.agents];
      return;
    }

    const query = this.searchQuery.toLowerCase();
    this.filteredAgents = this.agents.filter(agent => 
      agent.name?.toLowerCase().includes(query) || 
      agent.description?.toLowerCase().includes(query)
    );
  }

  async createNewAgent() {
    this.isCreatingNew = true;
    this.selectedAgent = null;
    this.agentRunner = await this.client.ai.createAgentRunner('');
    
    // Reset form with default values
    this.agentForm.reset({
      name: '',
      description: '',
      llmVendor: '',
      llmModel: '',
      systemPrompt: '',
      topP: 0,
      temperature: 0.7,
      maxPromptTokens: null,
      maxCompletionTokens: null,
      vectorStoreId: '',
      vendorAssistantIsShared: false,
      vendorAssistantId: null,
      vectorStoreIsShared: true,
      promptCache: false,
      asTool: false,
      toolChoice: 'auto',
      reasoningEffort: null,
      tools: [],
      contextCompact: 'none',
      manualUpdate: false,
      templateKeys: ''
    });
  }

  async selectAgent(agent: AiAgent) {
    if (!agent) {
      return;
    }
    this.isCreatingNew = false;
    this.selectedAgent = agent;
    this.agentRunner = await this.client.ai.createAgentRunner(agent.name);
    await this.agentRunner.load();
    await this.getModels(agent.llmVendor as LlmVendor);

    const data = {
      name: agent.name,
      description: agent.description,
      llmVendor: agent.llmVendor,
      llmModel: agent.llmModel,
      systemPrompt: agent.systemPrompt,
      topP: agent.topP,
      temperature: agent.temperature,
      maxPromptTokens: agent.maxPromptTokens,
      maxCompletionTokens: agent.maxCompletionTokens,
      vectorStoreId: agent.vectorStore?._id || agent.vectorStore,
      vendorAssistantIsShared: agent.vendorAssistantIsShared,
      vendorAssistantId: agent.vendorAssistantId,
      vectorStoreIsShared: agent.vectorStoreIsShared,
      promptCache: agent.promptCache,
      asTool: agent.asTool,
      toolChoice: agent.toolChoice,
      reasoningEffort: (agent as any).reasoningEffort || null,
      tools: agent.tools?.map(t => t._id) || [],
      contextCompact: agent.contextCompact || 'none',
      manualUpdate: agent.manualUpdate || false,
      templateKeys: (agent as any).templateKeys ? (agent as any).templateKeys.join(',') : ''
    }

    // Update form with agent data
    this.agentForm.patchValue(data, { emitEvent: false });

    // Add agent_id to the url
    // this.utilService.addUrlParamWithoutTriggeringNavigation('agent_id', agent._id?.toString());
    this.router.navigate([], {
      queryParams: { 'agent_id': agent._id?.toString() },
      queryParamsHandling: 'merge'
    });
    
    this.cdRef.detectChanges();
  }

  async createAgent() {
    if (!this.agentForm.valid) {
      return;
    }

    // remove the agent_id from the url
    this.utilService.removeUrlParamWithoutTriggeringNavigation('agent_id');

    try {
      this.loading = true;
      const formValue = this.agentForm.value;
      
      // Update the agentRunner with form values
      Object.keys(formValue).forEach(key => {
        if (key === 'templateKeys') {
          // Convert comma-separated string to array
          this.agentRunner[key] = formValue[key] ? formValue[key].split(/\s*,\s*/).map((k: string) => k.trim()).filter((k: string) => k) : [];
        } else {
          this.agentRunner[key] = formValue[key];
        }
      });

      // Save the agent using the agentRunner
      await this.agentRunner.save();
      
      this.snackBar.open('Agent created successfully', 'Close', {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
      });

      // Refresh the agent list and select the new agent
      await this.getAgentList();
      const newAgent = this.agents.find(a => a.name === formValue.name);
      if (newAgent) {
        await this.selectAgent(newAgent);
      }
      
      this.isCreatingNew = false;
    } catch (error) {
      console.error('Error creating agent:', error);
      this.snackBar.open('Error creating agent', 'Close', {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
      });
    } finally {
      this.loading = false;
    }
  }

  async updateAgent() {
    if (!this.selectedAgent || !this.agentForm.valid || !this.agentForm.dirty) {
      return;
    }

    this.loading = true;
    const formValue = this.agentForm.value;

    // Update the agentRunner with changed values
    Object.keys(formValue).forEach(key => {
      if (this.agentForm.get(key).dirty && key !== 'name') {
        if (key === 'templateKeys') {
          // Convert comma-separated string to array
          const templateKeysArray = formValue[key] ? formValue[key].split(/\s*,\s*/).map((k: string) => k.trim()).filter((k: string) => k) : [];
          this.selectedAgent[key] = templateKeysArray;
          this.agentRunner[key] = templateKeysArray;
        } else {
          this.selectedAgent[key] = formValue[key];
          this.agentRunner[key] = formValue[key];
        }
      }
    });

    this.agentRunner.tools = formValue.tools;

    try {
      // Update the agent using the agentRunner
      await this.agentRunner.save();

      this.snackBar.open('Agent updated successfully', 'Close', {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
      });
    } catch (error) {
      console.error('Error updating agent:', error);
      this.snackBar.open('Error updating agent', 'Close', {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
      });
    } finally {
      this.loading = false;
    }
  }

  async deleteAgent() {
    if (!this.selectedAgent) {
      return;
    }

    const confirmDialogData: ConfirmData = {
      title: 'Warning',
      subTitle: 'Do you really want to delete this agent?',
      okBtn: {
        name: 'Yes',
        value: 'confirm',
      },
      cancelBtn: {
        name: 'No',
        value: 'cancel',
      },
    };
    const dialog = this.dialog.open(ConfirmComponent, { data: confirmDialogData });
    dialog.afterClosed().subscribe(async (ret) => {
      if (ret === 'confirm') {
        await this.agentRunner.delete();
        await this.getAgentList();

        // select the first agent
        if (this.agents.length > 0) {
          await this.selectAgent(this.agents[0]);
        }
      }
    });
  }

  selectedFile: File = null;

  onFileSelected(event) {
    this.selectedFile = event.target.files[0];
  }

  uploadFile() {
    if (this.selectedFile) {
      console.log('Uploading file:', this.selectedFile.name);
      // Implement file upload logic here
    } else {
      console.log('No file selected');
    }
  }

  addTool(event: Event) {
    const currentTools = this.agentForm.get('tools').value;
    this.agentForm.patchValue({
      tools: [...currentTools, null]
    });
    this.agentForm.markAsDirty();
    event.preventDefault();
    event.stopPropagation();
  }

  removeTool(index: number) {
    const currentTools = this.agentForm.get('tools').value;
    currentTools.splice(index, 1);
    this.agentForm.patchValue({
      tools: currentTools
    });
    this.agentForm.markAsDirty();
  }

  updateTools() {
    // This is needed to mark the form as dirty when a tool is selected
    this.agentForm.markAsDirty();
  }

  openSystemPromptModal() {
    const markdown = this.agentForm.get('systemPrompt').value || '';
    this.dialog.open(MarkdownModalComponent, {
      data: { markdown } as MarkdownModalData,
      width: '700px',
      maxWidth: '95vw',
    });
  }
}
